import React, { useState, useRef, useEffect, useMemo } from "react";
import { onSocket, offSocket } from "services/websocket";
import { getReadableErrorMessage } from "shared/utils/errorMessageMappings";
import { getNotificationsListAPI, updateNotificationStatusAPI } from "api/notification";
import { getTimeAgo } from "shared/utils/dateHelpers";
import { useNavigate } from "react-router-dom";
import { notify } from "shared/utils/notify";
import { useSelector } from "react-redux";
import { fieldContainsValue } from "controllers/common";
import BellIcon from "components/ui/icons/svgs/BellIcon";
import ThreeDotsLoader from "components/ui/icons/svgs/ThreeDotsLoader";
import { useAgentAccountDetails } from "shared/hooks/common";
export default function Notifications({ showBellPermanent = false }) {
  const [isRinging, setIsRinging] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [maxPage, setMaxPage] = useState(0);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const reduxState = useSelector((state) => state);
  // const userRole = useMemo(() => reduxState?.role?.roles[0], [reduxState?.role?.roles]);
  const { agentRole } = useAgentAccountDetails();
  const userRole = agentRole;

  const mapping = {
    penalty_noti: "Penalty Request",
    waiver_noti: "Waiver Request",
    recovery_noti: "Recovery Request",
    car_drop_noti: "Car Drop Request",
    car_change_noti: "Car Change Request",
    maintenance_noti: "Maintenance Request",
    scheme_change_noti: "Scheme Change Request",
    driver_assign_to_dm: "Driver Assigned to DM",
    phone_unmask_alert: "Phone Unmask Alert",
  };
  const linkMapping = {
    penalty_noti: "/dashboard/penalty",
    waiver_noti: "/dashboard/waiver",
    recovery_noti: "/dashboard/recovery",
    car_drop_noti: "/dashboard/car_drop",
    car_change_noti: "/dashboard/car_change",
    maintenance_noti: "/dashboard/maintenance",
    scheme_change_noti: "/dashboard/rental_scheme_change",
  };
  const notificationRef = useRef(null);
  const bellRef = useRef(null);

  const scrollContainerRef = useRef(null);
  const getNotificationList = async (query) => {
    setLoadingMore(true);

    const container = scrollContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;

    try {
      const result = await getNotificationsListAPI(query);
      const notiList = result?.results?.notifications;
      if (result?.statusCode === 200) {
        const transformedList = transformList(notiList);

        setNotifications((prev) => [...prev, ...transformedList]);

        setMaxPage(result?.results?.pagination?.totalPages);
        setTotalUnread(result?.results?.pagination?.unreadCount);
        if (result?.results?.pagination?.unreadCount > 0) {
          // setIsRinging(true)
          setInterval(() => {
            setIsRinging((prev) => !prev);
          }, 1500);
        } else {
          setIsRinging(false);
        }

        // Restore scroll position AFTER DOM updates
        requestAnimationFrame(() => {
          if (container && page > 1) {
            const newScrollHeight = container.scrollHeight;
            // container.scrollBottom += newScrollHeight - prevScrollHeight;
          }
        });
      }
    } catch (error) {
      const message = getReadableErrorMessage(error);
      notify.error({
        message: "Unable to load notification list.",
        description: message,
        duration: 0,
        placement: "bottomRight",
      });
    }

    setLoadingMore(false);
  };

  const transformList = (data) => {
    const d = data?.map((item) => {
      const i = item;
      return {
        id: item?.id,
        message: item?.noti_msg,
        title: mapping[item?.message_type] || item?.message_type,
        isRead: item?.status === "seen",
        time: getTimeAgo(item?.createdAt),
        link: linkMapping[item?.message_type],
      };
    });
    return d;
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingMore(true);
        await getNotificationList(`page=${page}&limit=${pageSize}`);
      } finally {
        setLoadingMore(false);
      }
    };
    if (fieldContainsValue(userRole) && showNotifications) {
      fetchData();
    }
  }, [page, pageSize, userRole, showNotifications]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    onSocket("NEW_REQUEST_CREATE", handleNotification);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      offSocket("NEW_REQUEST_CREATE", handleNotification);
    };
  }, []);
  const calculatePosition = () => {
    if (!bellRef.current) return { left: 0 };

    const bellRect = bellRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 350;

    const spaceRight = viewportWidth - bellRect.right; // space on right
    const spaceLeft = bellRect.left; // space on left

    let left;

    if (spaceRight >= dropdownWidth) {
      // Enough space to the right → stick to bell's right
      left = bellRect.right + 10;
    } else if (spaceLeft >= dropdownWidth) {
      // Enough space to the left → align left of dropdown with left space
      left = bellRect.left - dropdownWidth;
    } else {
      // Not enough space either side → clamp inside viewport
      left = Math.max(0, Math.min(bellRect.left, viewportWidth - dropdownWidth));
    }

    return { left };
  };

  const handleNotification = (data) => {
    const noti = data?.data;

    //   notifyUser();
    playBrowserNotificationSound();
    const newNotification = {
      id: noti?.id,
      title: mapping[noti?.message_type] || noti?.message_type,
      message: noti?.message,
      time: getTimeAgo(noti?.createdAt),
      isRead: false,
      link: linkMapping[noti?.message_type],
    };

    // Add new notification to the beginning of the list
    setNotifications((prev) => [newNotification, ...prev]);

    // Start ringing animation for new notifications
    setIsRinging(true);

    // Stop ringing after 3 seconds
    setTimeout(() => {
      setIsRinging(false);
    }, 3000);
  };
  const handlePagination = () => {
    if (page < maxPage) {
      setPage((prevPage) => prevPage + 1);
    }
  };
  const playBrowserNotificationSound = () => {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        const audio = new Audio("/assets/sounds/live-chat-353605.mp3");
        audio.play().catch((err) => {
          console.warn("Audio playback failed:", err);
        });
      }
    });
  };
  const handleBellClick = () => {
    const newPosition = calculatePosition();
    setDropdownPosition(newPosition);
    setShowNotifications(!showNotifications);
    setIsRinging(false); // Stop ringing when clicked
  };
  const markNotificationAsRead = async (id) => {
    try {
      const result = await updateNotificationStatusAPI({
        notification_ids: [id],
        status: "seen",
      });
      if (result?.statusCode === 200) {
        // setNotifications(notifications)
        setNotifications(
          notifications?.map((item) => {
            if (item.id === id) {
              return { ...item, isRead: true };
            }
            return item;
          })
        );
        setTotalUnread((prev) => prev - 1);
      } else {
        setTimeout(() => {
          markNotificationAsRead();
        }, 2000);
      }
    } catch (error) {
      const message = getReadableErrorMessage(error);
      notify.error({
        message: "Unable to update seen status.",
        description: message,
        duration: 0,
        placement: "bottomRight",
      });
    }
  };
  const markNotificationAsUnread = async (id) => {
    try {
      const result = await updateNotificationStatusAPI({
        notification_ids: [id],
        status: "delivered",
      });
      if (result?.statusCode === 200) {
        // setNotifications(notifications)
        setNotifications(
          notifications?.map((item) => {
            if (item.id === id) {
              return { ...item, isRead: false };
            }
            return item;
          })
        );
        setTotalUnread((prev) => prev + 1);
      } else {
        setTimeout(() => {
          markNotificationAsRead();
        }, 2000);
      }
    } catch (error) {
      const message = getReadableErrorMessage(error);
      notify.error({
        message: "Unable to update unread status.",
        description: message,
        duration: 0,
        placement: "bottomRight",
      });
    }
  };

  return (
    <div className="w-fit">
      <div
        ref={bellRef}
        style={{
          width: "35px",
          height: "35px",
          borderRadius: "50%",
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
        onClick={handleBellClick}
        className="bg-red-500 shadow-md cursor-pointer"
      >
        {!showBellPermanent && (
          <span
            className="text-white text-xs cursor-pointer bell-ring"
            style={{
              animation: isRinging ? "ring 2s ease-in-out infinite" : "opacity 0.3s ease-in-out",
              transformOrigin: "top center",
              opacity: isRinging ? 1 : 0,
              transition: "opacity 0.3s ease-in-out, transform 0.6s ease-in-out",
              position: "absolute",
              transform: isRinging ? "rotateY(0deg)" : "rotateY(-180deg)",
              backfaceVisibility: "hidden",
            }}
          >
            <BellIcon size={25} color="#5f3aff"></BellIcon>
          </span>
        )}
        {!showBellPermanent && (
          <span
            style={{
              opacity: isRinging ? 0 : 1,
              transition: "opacity 0.3s ease-in-out, transform 0.6s ease-in-out",
              position: "absolute",
              // transform: isRinging ? 'rotateY(180deg)' : 'rotateY(0deg)',
              transform: isRinging ? "rotateY(-180deg)" : "",
              backfaceVisibility: "hidden",
            }}
          >
            <img src="/icons/Avatar.svg" alt="Company Logo" className="h-[38px] hidden md:block" />
          </span>
        )}
        {showBellPermanent && (
          <span
            className="text-white text-xs cursor-pointer bell-ring"
            style={{
              position: "absolute",
            }}
          >
            <BellIcon size={25} color="#5f3aff"></BellIcon>
          </span>
        )}
        {totalUnread > 0 && (
          <span
            style={{
              borderRadius: "50%",
              fontSize: "8px",
              padding: "2px",
              top: "-6px",
              right: "-6px",
              position: "absolute",
              width: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            className="bg-red-500 text-white text-xs  "
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </div>

      {showNotifications && (
        <div
          ref={notificationRef}
          style={{
            position: "fixed",
            // top: dropdownPosition.top,
            top: "1%",
            bottom: 0,
            left: dropdownPosition.left,
            width: "350px",
            maxHeight: "99%",
            height: "98%",
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            border: "1px solid #e5e7eb",
            zIndex: 9999,
            overflow: "hidden",
          }}
          className="bg-red-500"
        >
          {/* Header */}

          <div
            style={{
              padding: "16px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Notifications ({totalUnread})
              </h3>
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  cursor: "pointer",
                }}
                onClick={() => setShowNotifications(false)}
              >
                ✕
              </span>
            </div>
          </div>

          {/* Notification Items */}
          {notifications.length > 0 && (
            <div
              ref={scrollContainerRef}
              style={{
                height: page < maxPage || loadingMore ? "86%" : "90%",
                overflowY: "scroll",
              }}
              className="custom-scrollbar red-500 "
            >
              {notifications.map((notification, index) => (
                <div
                  id="notification-item"
                  key={index}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f3f4f6",
                    // cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  className={`${
                    notification?.isRead
                      ? "bg-white hover:bg-gray-200"
                      : "bg-[#ebe9f7] hover:bg-[#CBCAFF]"
                  }`}
                >
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      markNotificationAsRead(notification?.id);
                      navigate(notification?.link);
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "4px",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: notification.isRead ? "400" : "600",
                          color: "#374151",
                        }}
                      >
                        {notification.title}
                      </h4>
                      {!notification.isRead && (
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: "#5a3dfd",
                            marginLeft: "8px",
                          }}
                        ></div>
                      )}
                    </div>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "12px",
                        color: "#6b7280",
                        lineHeight: "1.4",
                      }}
                      className="hover:bg-transparent"
                    >
                      {notification.message}
                    </p>
                  </div>

                  <p className="flex justify-between items-center">
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        marginTop: "4px",
                        display: "block",
                      }}
                      className="hover:bg-transparent"
                    >
                      {notification.time || "now"}
                    </span>
                    {notification.isRead && (
                      <span
                        style={{
                          fontSize: "11px",

                          marginTop: "4px",
                          display: "block",
                        }}
                        onClick={(e) => {
                          markNotificationAsUnread(notification?.id);
                        }}
                        className="cursor-pointer text-blue-500"
                      >
                        Mark as Unread
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div
            style={{}}
            className="py-2 text-center text-blue-600 cursor-pointer flex justify-center items-center tex-sm"
          >
            {!loadingMore && page < maxPage && <span onClick={handlePagination}> Load More</span>}
            {loadingMore && <ThreeDotsLoader size="45"></ThreeDotsLoader>}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes ring {
          0%,
          100% {
            transform: rotate(0deg);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: rotate(5deg);
          }
          20%,
          40%,
          60%,
          80% {
            transform: rotate(-5deg);
          }
        }

        .bell-ring:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}
